import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, FileTextIcon, CheckCircleIcon, ArrowLeftIcon, UploadIcon, UserIcon, BuildingIcon } from 'lucide-react';
import { useTransactions } from '../contexts/TransactionContext';
import { useAuth } from '../contexts/AuthContext';
interface TransactionFormModalProps {
  transactionType: 'rental' | 'sale';
  transactionValue: number;
  agreedCommission: number;
  commissionedParty: string;
  sellerCommission?: number;
  buyerCommission?: number;
  landlordCommission?: number;
  tenantCommission?: number;
  sellerVAT?: number;
  buyerVAT?: number;
  landlordVAT?: number;
  tenantVAT?: number;
  sellerTotal?: number;
  buyerTotal?: number;
  landlordTotal?: number;
  tenantTotal?: number;
  vatOption?: string;
  sellerVATOption?: string;
  buyerVATOption?: string;
  landlordVATOption?: string;
  tenantVATOption?: string;
  onClose: () => void;
  onBack: () => void;
}
type ClientType = 'PF' | 'PJ';
export function TransactionFormModal({
  transactionType,
  transactionValue,
  agreedCommission,
  commissionedParty,
  sellerCommission,
  buyerCommission,
  landlordCommission,
  tenantCommission,
  sellerVAT,
  buyerVAT,
  landlordVAT,
  tenantVAT,
  sellerTotal,
  buyerTotal,
  landlordTotal,
  tenantTotal,
  vatOption,
  sellerVATOption,
  buyerVATOption,
  landlordVATOption,
  tenantVATOption,
  onClose,
  onBack
}: TransactionFormModalProps) {
  const {
    addTransaction
  } = useTransactions();
  const {
    user
  } = useAuth();
  const [ownerType, setOwnerType] = useState<ClientType>('PF');
  const [clientType, setClientType] = useState<ClientType>('PF');
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    transactionDate: new Date().toISOString().split('T')[0],
    agentName: user?.fullName || '',
    transactionCode: `TRX-${Date.now()}`,
    propertyAddress: '',
    // Owner data
    ownerName: '',
    ownerPhone: '',
    ownerCNP: '',
    ownerCI: '',
    ownerAddress: '',
    ownerCUI: '',
    ownerCompanyName: '',
    ownerCompanyAddress: '',
    // Client data (Tenant/Buyer)
    clientName: '',
    clientPhone: '',
    clientCNP: '',
    clientCI: '',
    clientAddress: '',
    clientCUI: '',
    clientCompanyName: '',
    clientCompanyAddress: '',
    notes: ''
  });
  const [ownerContract, setOwnerContract] = useState<File | null>(null);
  const [clientContract, setClientContract] = useState<File | null>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const transaction = {
      type: transactionType,
      transactionValue,
      agreedCommission,
      commissionedParty,
      clientName: clientType === 'PF' ? formData.clientName : formData.clientCompanyName,
      clientEmail: '',
      clientPhone: formData.clientPhone,
      propertyAddress: formData.propertyAddress,
      propertyDetails: `${transactionType === 'rental' ? 'Închiriere' : 'Vânzare'} - ${formData.propertyAddress}`,
      notes: formData.notes,
      sellerCommission,
      buyerCommission,
      landlordCommission,
      tenantCommission,
      sellerVAT,
      buyerVAT,
      landlordVAT,
      tenantVAT,
      sellerTotal,
      buyerTotal,
      landlordTotal,
      tenantTotal,
      vatOption,
      sellerVATOption,
      buyerVATOption,
      landlordVATOption,
      tenantVATOption,
      agentName: formData.agentName,
      transactionCode: formData.transactionCode,
      ownerType,
      clientType,
      // Owner data - PF
      ownerName: ownerType === 'PF' ? formData.ownerName : undefined,
      ownerPhone: formData.ownerPhone,
      ownerCNP: ownerType === 'PF' ? formData.ownerCNP : undefined,
      ownerCI: ownerType === 'PF' ? formData.ownerCI : undefined,
      ownerAddress: ownerType === 'PF' ? formData.ownerAddress : undefined,
      // Owner data - PJ
      ownerCUI: ownerType === 'PJ' ? formData.ownerCUI : undefined,
      ownerCompanyName: ownerType === 'PJ' ? formData.ownerCompanyName : undefined,
      ownerCompanyAddress: ownerType === 'PJ' ? formData.ownerCompanyAddress : undefined,
      // Client data - PF
      clientCNP: clientType === 'PF' ? formData.clientCNP : undefined,
      clientCI: clientType === 'PF' ? formData.clientCI : undefined,
      clientAddress: clientType === 'PF' ? formData.clientAddress : undefined,
      // Client data - PJ
      clientCUI: clientType === 'PJ' ? formData.clientCUI : undefined,
      clientCompanyName: clientType === 'PJ' ? formData.clientCompanyName : undefined,
      clientCompanyAddress: clientType === 'PJ' ? formData.clientCompanyAddress : undefined,
      // Contracts
      ownerContract: ownerContract?.name,
      clientContract: clientContract?.name
    };
    addTransaction(transaction);
    setShowSuccess(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'owner' | 'client') => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Fișierul este prea mare. Dimensiunea maximă este 10MB.');
        return;
      }
      if (type === 'owner') {
        setOwnerContract(file);
      } else {
        setClientContract(file);
      }
    }
  };
  const totalToBill = sellerTotal && buyerTotal ? sellerTotal + buyerTotal : landlordTotal && tenantTotal ? landlordTotal + tenantTotal : sellerTotal || buyerTotal || landlordTotal || tenantTotal || 0;
  const agentCommission = agreedCommission * 0.5;
  return <AnimatePresence data-id="element-6886">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-6887">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8" data-id="element-6888">
          {showSuccess ? <div className="p-12 text-center" data-id="element-6889">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-6890">
                <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-6891" />
              </motion.div>
              <h2 className="text-2xl font-bold text-[#132C53] mb-2" data-id="element-6892">
                Tranzacție salvată și trimisă către facturare!
              </h2>
              <p className="text-gray-600" data-id="element-6893">
                Tranzacția a fost adăugată cu succes și este disponibilă în
                Dashboard și pagina Tranzacții.
              </p>
            </div> : <>
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl z-10" data-id="element-6894">
                <div className="flex items-center space-x-3" data-id="element-6895">
                  <FileTextIcon className="w-6 h-6 text-[#357BEB]" data-id="element-6896" />
                  <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-6897">
                    Adăugare Tranzacție
                  </h2>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6898">
                  <XIcon className="w-6 h-6 text-gray-500" data-id="element-6899" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto" data-id="element-6900">
                {/* I. Transaction Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200" data-id="element-6901">
                  <h3 className="font-bold text-[#132C53] mb-4 text-lg" data-id="element-6902">
                    I. Rezumat Tranzacție
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-6903">
                    <div data-id="element-6904">
                      <p className="text-gray-600" data-id="element-6905">Tip tranzacție</p>
                      <p className="font-bold text-[#132C53]" data-id="element-6906">
                        {transactionType === 'rental' ? 'Închiriere' : 'Vânzare'}
                      </p>
                    </div>
                    <div data-id="element-6907">
                      <p className="text-gray-600" data-id="element-6908">Valoare</p>
                      <p className="font-bold text-[#132C53]" data-id="element-6909">
                        {transactionValue.toLocaleString('ro-RO')}€
                      </p>
                    </div>
                    <div data-id="element-6910">
                      <p className="text-gray-600" data-id="element-6911">Comision total</p>
                      <p className="font-bold text-[#357BEB]" data-id="element-6912">
                        {agreedCommission.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                        €
                      </p>
                    </div>
                    <div data-id="element-6913">
                      <p className="text-gray-600" data-id="element-6914">Total de facturat</p>
                      <p className="font-bold text-[#21A366]" data-id="element-6915">
                        {totalToBill.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                        €
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-blue-300 grid grid-cols-2 gap-4 text-sm" data-id="element-6916">
                    <div data-id="element-6917">
                      <p className="text-gray-600" data-id="element-6918">Comision agent (50%)</p>
                      <p className="font-bold text-green-700" data-id="element-6919">
                        {agentCommission.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                        €
                      </p>
                    </div>
                    <div data-id="element-6920">
                      <p className="text-gray-600" data-id="element-6921">Comision agenție (50%)</p>
                      <p className="font-bold text-purple-700" data-id="element-6922">
                        {agentCommission.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                        €
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm" data-id="element-6923">
                    <div data-id="element-6924">
                      <p className="text-gray-600" data-id="element-6925">Data tranzacției</p>
                      <p className="font-bold text-[#132C53]" data-id="element-6926">
                        {formData.transactionDate}
                      </p>
                    </div>
                    <div data-id="element-6927">
                      <p className="text-gray-600" data-id="element-6928">Agent</p>
                      <p className="font-bold text-[#132C53]" data-id="element-6929">
                        {formData.agentName}
                      </p>
                    </div>
                  </div>
                </div>
                {/* II. Property Address */}
                <div data-id="element-6930">
                  <h3 className="font-bold text-[#132C53] mb-3 text-lg" data-id="element-6931">
                    II. Adresa Completă a Imobilului Tranzacționat
                  </h3>
                  <input type="text" name="propertyAddress" value={formData.propertyAddress} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: Str. Exemplu nr. 10, Sector 1, București" data-id="element-6932" />
                </div>
                {/* III. Owner Data */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200" data-id="element-6933">
                  <h3 className="font-bold text-[#132C53] mb-4 text-lg flex items-center" data-id="element-6934">
                    <UserIcon className="w-5 h-5 mr-2" data-id="element-6935" />
                    III. Date Client -{' '}
                    {transactionType === 'rental' ? 'Proprietar' : 'Vânzător'}
                  </h3>
                  <div className="flex space-x-2 mb-4" data-id="element-6936">
                    <button type="button" onClick={() => setOwnerType('PF')} className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${ownerType === 'PF' ? 'bg-[#357BEB] text-white' : 'bg-white text-gray-700 border border-gray-300'}`} data-id="element-6937">
                      Persoană Fizică
                    </button>
                    <button type="button" onClick={() => setOwnerType('PJ')} className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${ownerType === 'PJ' ? 'bg-[#357BEB] text-white' : 'bg-white text-gray-700 border border-gray-300'}`} data-id="element-6938">
                      Persoană Juridică
                    </button>
                  </div>
                  {ownerType === 'PF' ? <div className="space-y-4" data-id="element-6939">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6940">
                        <div data-id="element-6941">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6942">
                            Nume complet *
                          </label>
                          <input type="text" name="ownerName" value={formData.ownerName} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: Ion Popescu" data-id="element-6943" />
                        </div>
                        <div data-id="element-6944">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6945">
                            Telefon *
                          </label>
                          <input type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: 0721 XXX XXX" data-id="element-6946" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6947">
                        <div data-id="element-6948">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6949">
                            CNP *
                          </label>
                          <input type="text" name="ownerCNP" value={formData.ownerCNP} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: 1234567890123" maxLength={13} data-id="element-6950" />
                        </div>
                        <div data-id="element-6951">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6952">
                            Serie CI *
                          </label>
                          <input type="text" name="ownerCI" value={formData.ownerCI} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: AB 123456" data-id="element-6953" />
                        </div>
                      </div>
                      <div data-id="element-6954">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6955">
                          Adresă domiciliu *
                        </label>
                        <input type="text" name="ownerAddress" value={formData.ownerAddress} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: Str. Exemplu nr. 5, București" data-id="element-6956" />
                      </div>
                    </div> : <div className="space-y-4" data-id="element-6957">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6958">
                        <div data-id="element-6959">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6960">
                            Denumire companie *
                          </label>
                          <input type="text" name="ownerCompanyName" value={formData.ownerCompanyName} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: SC Exemplu SRL" data-id="element-6961" />
                        </div>
                        <div data-id="element-6962">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6963">
                            CUI *
                          </label>
                          <input type="text" name="ownerCUI" value={formData.ownerCUI} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: RO12345678" data-id="element-6964" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6965">
                        <div data-id="element-6966">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6967">
                            Telefon *
                          </label>
                          <input type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: 0721 XXX XXX" data-id="element-6968" />
                        </div>
                        <div data-id="element-6969">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6970">
                            Adresă sediu social *
                          </label>
                          <input type="text" name="ownerCompanyAddress" value={formData.ownerCompanyAddress} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="ex: Str. Exemplu nr. 5, București" data-id="element-6971" />
                        </div>
                      </div>
                    </div>}
                </div>
                {/* IV. Client Data (Tenant/Buyer) */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200" data-id="element-6972">
                  <h3 className="font-bold text-[#132C53] mb-4 text-lg flex items-center" data-id="element-6973">
                    <UserIcon className="w-5 h-5 mr-2" data-id="element-6974" />
                    IV. Date Client -{' '}
                    {transactionType === 'rental' ? 'Chiriaș' : 'Cumpărător'}
                  </h3>
                  <div className="flex space-x-2 mb-4" data-id="element-6975">
                    <button type="button" onClick={() => setClientType('PF')} className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${clientType === 'PF' ? 'bg-[#21A366] text-white' : 'bg-white text-gray-700 border border-gray-300'}`} data-id="element-6976">
                      Persoană Fizică
                    </button>
                    <button type="button" onClick={() => setClientType('PJ')} className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${clientType === 'PJ' ? 'bg-[#21A366] text-white' : 'bg-white text-gray-700 border border-gray-300'}`} data-id="element-6977">
                      Persoană Juridică
                    </button>
                  </div>
                  {clientType === 'PF' ? <div className="space-y-4" data-id="element-6978">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6979">
                        <div data-id="element-6980">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6981">
                            Nume complet *
                          </label>
                          <input type="text" name="clientName" value={formData.clientName} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: Maria Ionescu" data-id="element-6982" />
                        </div>
                        <div data-id="element-6983">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6984">
                            Telefon *
                          </label>
                          <input type="tel" name="clientPhone" value={formData.clientPhone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: 0721 XXX XXX" data-id="element-6985" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6986">
                        <div data-id="element-6987">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6988">
                            CNP *
                          </label>
                          <input type="text" name="clientCNP" value={formData.clientCNP} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: 2987654321098" maxLength={13} data-id="element-6989" />
                        </div>
                        <div data-id="element-6990">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6991">
                            Serie CI *
                          </label>
                          <input type="text" name="clientCI" value={formData.clientCI} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: CD 987654" data-id="element-6992" />
                        </div>
                      </div>
                      <div data-id="element-6993">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6994">
                          Adresă domiciliu *
                        </label>
                        <input type="text" name="clientAddress" value={formData.clientAddress} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: Str. Altă Adresă nr. 15, București" data-id="element-6995" />
                      </div>
                    </div> : <div className="space-y-4" data-id="element-6996">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6997">
                        <div data-id="element-6998">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6999">
                            Denumire companie *
                          </label>
                          <input type="text" name="clientCompanyName" value={formData.clientCompanyName} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: SC Altă Companie SRL" data-id="element-7000" />
                        </div>
                        <div data-id="element-7001">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7002">
                            CUI *
                          </label>
                          <input type="text" name="clientCUI" value={formData.clientCUI} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: RO98765432" data-id="element-7003" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7004">
                        <div data-id="element-7005">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7006">
                            Telefon *
                          </label>
                          <input type="tel" name="clientPhone" value={formData.clientPhone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: 0721 XXX XXX" data-id="element-7007" />
                        </div>
                        <div data-id="element-7008">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7009">
                            Adresă sediu social *
                          </label>
                          <input type="text" name="clientCompanyAddress" value={formData.clientCompanyAddress} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#21A366] focus:border-transparent" placeholder="ex: Str. Altă Adresă nr. 15, București" data-id="element-7010" />
                        </div>
                      </div>
                    </div>}
                </div>
                {/* V. Contract Uploads */}
                <div data-id="element-7011">
                  <h3 className="font-bold text-[#132C53] mb-4 text-lg" data-id="element-7012">
                    V. Contracte Semnate
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7013">
                    {/* Owner Contract */}
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-[#357BEB] transition-colors" data-id="element-7014">
                      <label className="cursor-pointer block" data-id="element-7015">
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => handleFileUpload(e, 'owner')} className="hidden" data-id="element-7016" />
                        <div className="text-center" data-id="element-7017">
                          <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-7018" />
                          <p className="font-semibold text-gray-700 mb-1" data-id="element-7019">
                            📄 Contract{' '}
                            {transactionType === 'rental' ? 'Proprietar' : 'Vânzător'}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-7020">
                            Click pentru a încărca
                          </p>
                          <p className="text-xs text-gray-400" data-id="element-7021">
                            PDF, DOC, DOCX (max 10MB)
                          </p>
                          {ownerContract && <div className="mt-3 p-2 bg-green-50 rounded-lg" data-id="element-7022">
                              <p className="text-sm text-green-700 font-medium truncate" data-id="element-7023">
                                ✓ {ownerContract.name}
                              </p>
                            </div>}
                        </div>
                      </label>
                    </div>
                    {/* Client Contract */}
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-[#21A366] transition-colors" data-id="element-7024">
                      <label className="cursor-pointer block" data-id="element-7025">
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => handleFileUpload(e, 'client')} className="hidden" data-id="element-7026" />
                        <div className="text-center" data-id="element-7027">
                          <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-7028" />
                          <p className="font-semibold text-gray-700 mb-1" data-id="element-7029">
                            📄 Contract{' '}
                            {transactionType === 'rental' ? 'Chiriaș' : 'Cumpărător'}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-7030">
                            Click pentru a încărca
                          </p>
                          <p className="text-xs text-gray-400" data-id="element-7031">
                            PDF, DOC, DOCX (max 10MB)
                          </p>
                          {clientContract && <div className="mt-3 p-2 bg-green-50 rounded-lg" data-id="element-7032">
                              <p className="text-sm text-green-700 font-medium truncate" data-id="element-7033">
                                ✓ {clientContract.name}
                              </p>
                            </div>}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                {/* Notes */}
                <div data-id="element-7034">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7035">
                    Observații (opțional)
                  </label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#357BEB] focus:border-transparent" placeholder="Adaugă orice informații suplimentare despre tranzacție..." data-id="element-7036" />
                </div>
              </form>
              {/* Submit Button */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl" data-id="element-7037">
                <button onClick={handleSubmit} className="w-full bg-gradient-to-r from-[#21A366] to-[#1a8555] text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2" data-id="element-7038">
                  <FileTextIcon className="w-6 h-6" data-id="element-7039" />
                  <span data-id="element-7040">Trimite cerere de facturare</span>
                </button>
              </div>
            </>}
        </motion.div>
      </div>
    </AnimatePresence>;
}